import { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { renderRoutes } from 'react-router-config';
import classNames from 'clsx';

import { LocationAndLocalizationContext } from '../providers/LocationAndLocalizationProvider';
import getTopBannerOffsetStyles from '../utils/banner/getTopBannerOffsetStyles';
import hideHeaderHelper from '../utils/hideHeaderHelper';
import { isLarge } from '../utils/breakpoints';
import {
  selectIsDesktop,
  selectIsMobile,
  selectTopBannerHeight,
} from '../selectors/app';
import { selectIsIdle } from '../selectors/player';
import { selectAuthViewType } from '../selectors/loginDetails';
import { selectShouldShowDesktopUpdateBanner } from '../selectors/desktop';
import { selectIsSubscriptionEnabled } from '../selectors/config';
import { ThemeProvider } from '../styles/muiThemes';
import determineAdEligibility from '../utils/ads/determineAdEligibility';
import useSideBar from '../hooks/useSideBar';
import GlobalDialogs from './globalDialogs/GlobalDialogs';
import Loader from './Loader';
import Header from './header/Header';
import ScrollReset from './shared/ScrollReset';
import InstallDesktopUpdateBanner from './desktop/InstallDesktopUpdateBanner';
import WithPlayer from './player/WithPlayerLoader';
import WithAds from './ads/WithAdsLoader';
import LeftSideLoader from './sidebar/LeftSideLoader';
import RightSide from './sidebar/RightSide';
import Footer from './footer/Footer';
import ConsentManager from './consent/ConsentManager';
import MessagePopover from './messagePopover/MessagePopover';

import appCss from './app.module.scss';

export default function AppRoot({
  breakpoint,
  route,
  routeProps = {},
  apiClient,
  history,
}) {
  const guideId = routeProps.guideContext?.guideId;
  const isMobile = useSelector(selectIsMobile);
  const isDesktop = useSelector(selectIsDesktop);
  const isIdle = useSelector(selectIsIdle);
  const isSubscriptionEnabled = useSelector(selectIsSubscriptionEnabled);
  const shouldShowDesktopUpdateBanner = useSelector(
    selectShouldShowDesktopUpdateBanner,
  );
  const authViewType = useSelector(selectAuthViewType);
  const topBannerHeight = useSelector(selectTopBannerHeight);
  const canShowAds = useSelector(determineAdEligibility(guideId));
  const {
    location: { pathname, search, query },
  } = useContext(LocationAndLocalizationContext);
  const {
    isRightSideOpen,
    isLeftSideDocked,
    isLeftSideOpen,
    handleLeftSideToggle,
  } = useSideBar(breakpoint, isMobile, routeProps);

  /**
   * We want to show the popover message when the app is in mobile view,
   * and also up to the Large breakpoint when the player is not playing, from there
   * the player instance of MessagePopover is the one that is displayed
   */
  function shouldShowPopover() {
    return isMobile || isIdle || !isLarge(breakpoint);
  }

  const backgroundColor = routeProps.bgColor
    ? { backgroundColor: routeProps.bgColor }
    : {};

  const contentStyles = {
    ...backgroundColor,
  };

  if (shouldShowDesktopUpdateBanner) {
    Object.assign(contentStyles, getTopBannerOffsetStyles(topBannerHeight));
  }

  const shouldHideHeader = useMemo(
    () => hideHeaderHelper(query, pathname, authViewType),
    [query, pathname, authViewType],
  );

  const showSidebarToggle = !routeProps.hideLeft && !isLeftSideDocked;

  const sidePadding = {
    [appCss.rightSideOpen]: !routeProps.hideRight ? isRightSideOpen : false,
    [appCss.leftSideOpen]: !routeProps.hideLeft ? isLeftSideOpen : false,
    [appCss.homeLeftSideOpen]: routeProps.isLanding,
  };

  const contentProps = {
    breakpoint,
    routeProps,
    history,
    canShowAds,
  };

  return (
    <div id="outerAppContent">
      <ThemeProvider>
        <div id="innerAppContent">
          {!routeProps.hidePlayer && (
            <WithPlayer
              apiClient={apiClient}
              history={history}
              breakpoint={breakpoint}
              routeProps={routeProps}
            />
          )}
          <ConsentManager />
          {!routeProps.hideAds && <WithAds routeProps={routeProps} />}
          {shouldShowDesktopUpdateBanner && <InstallDesktopUpdateBanner />}
          <ScrollReset
            id="appRootScrollReset"
            style={contentStyles}
            scrollHash={`${pathname}${search}`}
            className={classNames(appCss.app, {
              [appCss.isLanding]: routeProps.isLanding,
              [appCss.appWithPlayer]: !isIdle,
              [appCss.noHeader]: routeProps.noHeader || shouldHideHeader,
            })}
          >
            {!routeProps.noHeader && !shouldHideHeader && (
              <Header
                showSidebarToggle={showSidebarToggle}
                onSidebarToggle={handleLeftSideToggle}
                isLanding={routeProps.isLanding}
                isMinimalHeader={routeProps.isMinimalHeader}
                unlinkedLogo={routeProps.unlinkedLogo}
                sourceGuideId={routeProps.guideContext.guideId}
                isDesktop={isDesktop}
                isLeftSideOpen={isLeftSideOpen}
              />
            )}
            {!routeProps.hideRight && (
              <RightSide
                isRightSideOpen={isRightSideOpen}
                breakpoint={breakpoint}
                hideRightSidebarUpsell={routeProps.hideRightSidebarUpsell}
                matchUrl={routeProps.matchUrl}
                guideId={guideId}
                canShowAds={canShowAds}
              />
            )}
            {!routeProps.hideLeft && (
              <LeftSideLoader
                isLeftSideOpen={isLeftSideOpen}
                isLeftSideDocked={isLeftSideDocked}
                handleLeftSideToggle={handleLeftSideToggle}
                subscriptionEnabled={isSubscriptionEnabled}
                breakpoint={breakpoint}
                currentGuideId={routeProps.guideContext.guideId}
                history={history}
              />
            )}
            <Loader />
            <div
              className={classNames(appCss.content, {
                ...sidePadding,
                [appCss.isMinimalHeader]: routeProps.isMinimalHeader,
                [appCss.mobile]: isMobile,
                [appCss.isLanding]: routeProps.isLanding,
                [appCss.noHeader]: routeProps.noHeader || shouldHideHeader,
              })}
            >
              {renderRoutes(route.routes, contentProps)}
            </div>
            {!isDesktop && !routeProps.hideFooter && (
              <div className={classNames(sidePadding)}>
                <Footer
                  isRightSideOpen={isRightSideOpen}
                  isLanding={routeProps.isLanding}
                />
              </div>
            )}
            <GlobalDialogs
              breakpoint={breakpoint}
              routeProps={routeProps}
              history={history}
            />
          </ScrollReset>
          {shouldShowPopover() && (
            <MessagePopover
              isPlayerOpen={!isIdle}
              className={classNames({
                [appCss.messagePopoverOffset]: isIdle && isLarge(breakpoint),
              })}
            />
          )}
        </div>
      </ThemeProvider>
    </div>
  );
}

AppRoot.propTypes = {
  breakpoint: PropTypes.number.isRequired,
  route: PropTypes.object.isRequired,
  routeProps: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  apiClient: PropTypes.object,
};
